import {
  Status1Icon,
  Status2Icon,
  Status3Icon,
} from 'components/CustomIcons/StatusIcons'
import { onFormatDate } from 'components/DatePickers'
import {
  EntityChangeList,
  EntityChangeListProps,
} from 'components/EntityChangeList'
import { renderDate, renderEnum } from 'components/EntityList'
import { Box } from 'grid'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { Material, ProvingMaterial, ProvingType, TransportType } from 'model'
import {
  ConstrainMode,
  Icon,
  SelectionMode,
  TooltipHost,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'

export interface MaterialListRender {
  _id: string
  materialLabel: string
  amount: number
  transport: TransportType
  orderId: string
  annotation?: string
}
export type MaterialListProps = EntityChangeListProps<
  Partial<Material> & { _id: string }
>

export const MaterialList: React.FunctionComponent<MaterialListProps> = ({
  items,
  ...props
}) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()

  const renderTransportInformation = (material: ProvingMaterial) => {
    if (material.transport && material.transport.transportType) {
      const outboundSender = material.transport.outboundTransportSender
        ? material.transport.outboundTransportSender.place
        : ''
      const outboundRecipient = material.transport.outboundTransportRecipient
        ? material.transport.outboundTransportRecipient.place
        : ''
      const returnSender = material.transport.returnTransportSender
        ? material.transport.returnTransportSender.place
        : ''
      const returnRecipient = material.transport.returnTransportRecipient
        ? material.transport.returnTransportRecipient.place
        : ''
      const outboundSenderDate =
        material.transport.outboundTransportSenderDate || ''
      const outboundRecipientDate =
        material.transport.outboundTransportRecipientDate || ''
      const returnSenderDate =
        material.transport.returnTransportSenderDate || ''
      const returnRecipientDate =
        material.transport.returnTransportRecipientDate || ''
      const outboundSenderTime =
        material.transport.outboundTransportSenderTime || ''
      const outboundRecipientTime =
        material.transport.outboundTransportRecipientTime || ''
      const returnSenderTime =
        material.transport.returnTransportSenderTime || ''
      const returnRecipientTime =
        material.transport.returnTransportRecipientTime || ''
      switch (material.transport.transportType) {
        case TransportType.OutboundTransport:
          return (
            <div>
              {'Hin: '}
              <br />
              {outboundSender} {onFormatDate(new Date(outboundSenderDate))}{' '}
              {outboundSenderTime}
              {' - '}
              {outboundRecipient}{' '}
              {onFormatDate(new Date(outboundRecipientDate))}{' '}
              {outboundRecipientTime}
            </div>
          )
        case TransportType.ReturnTransport:
          return (
            <div>
              {'Rück: '}
              <br />
              {returnSender}{' '}
              {returnSenderDate ? onFormatDate(new Date(returnSenderDate)) : ''}{' '}
              {returnSenderTime}
              {' - '}
              {returnRecipient}{' '}
              {returnRecipientDate
                ? onFormatDate(new Date(returnRecipientDate))
                : ''}{' '}
              {returnRecipientTime}
            </div>
          )
        case TransportType.OutboundAndReturnTransport:
          return (
            <div>
              {'Hin: '}
              <br />
              {outboundSender}{' '}
              {outboundSenderDate
                ? onFormatDate(new Date(outboundSenderDate))
                : ''}{' '}
              {outboundSenderTime}
              {' - '}
              {outboundRecipient}{' '}
              {outboundRecipientDate
                ? onFormatDate(new Date(outboundRecipientDate))
                : ''}{' '}
              {outboundRecipientTime}
              <br />
              {'Rück: '}
              <br />
              {returnSender}{' '}
              {returnSenderDate ? onFormatDate(new Date(returnSenderDate)) : ''}{' '}
              {returnSenderTime}
              {' - '}
              {returnRecipient}{' '}
              {returnRecipientDate
                ? onFormatDate(new Date(returnRecipientDate))
                : ''}{' '}
              {returnRecipientTime}
            </div>
          )
        default:
          break
      }
    }
    return <></>
  }

  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])

  const columns = React.useMemo(
    () => [
      fieldColumn('material', {
        onRender: (item: ProvingMaterial, i, col) => {
          return (
            <>
              {' '}
              {item.isDangerousGood && (
                <>
                  <TooltipHost content="Gefahrengut">
                    <Icon
                      styles={{
                        root: { color: 'red', margin: 'auto' },
                      }}
                      iconName="Warning"
                    />
                  </TooltipHost>
                  &nbsp;
                </>
              )}
              {item.material!.label}
            </>
          )
        },
        isSorted: true,

        ungroupable: true,
      }),
      fieldColumn('dateFrom', { onRender: renderDate, ungroupable: true }),
      fieldColumn('dateTo', { onRender: renderDate, ungroupable: true }),
      fieldColumn('amount', {
        ungroupable: true,
      }),
      fieldColumn('orderId', { minWidth: 100 }),
      fieldColumn('transport.transportType', {
        onRender: (item: ProvingMaterial, i, col) => {
          if (item.transport && item.transport.bookingStatus) {
            switch (item.transport!.bookingStatus) {
              case 'open':
                return (
                  <Box>
                    <Status1Icon />{' '}
                    {renderEnum(t, 'transportType')(item, i, col)}
                  </Box>
                )
              case 'workInProgress':
                return (
                  <Box>
                    <Status2Icon />{' '}
                    {renderEnum(t, 'transportType')(item, i, col)}
                  </Box>
                )
              case 'finished':
                return (
                  <Box>
                    <Status3Icon />{' '}
                    {renderEnum(t, 'transportType')(item, i, col)}
                  </Box>
                )
              default:
                return renderEnum(t, 'transportType')(item, i, col)
            }
          } else {
            return renderEnum(t, 'transportType')(item, i, col)
          }
        },
        minWidth: 120,
        ungroupable: true,
      }),
    ],
    [fieldColumn, t]
  )
  const transportInformationColumn = React.useMemo(
    () =>
      fieldColumn('transport', {
        customFieldName: 'Transportinformationen',
        ungroupable: true,
        onRender: renderTransportInformation,
        minWidth: 240,
      }),
    [fieldColumn]
  )

  if (proving.type !== ProvingType.Block) {
    columns.push(transportInformationColumn)
  }
  columns.push(fieldColumn('annotation', { ungroupable: true }))

  return (
    <EntityChangeList
      alternate={true}
      stickyHeader={true}
      selectionMode={SelectionMode.multiple}
      items={items}
      parentGroup={
        (proving.type && proving.type === ProvingType.Block) ||
        proving.enableGroups
          ? 'group'
          : undefined
      }
      onShouldVirtualize={() => false}
      noParentGroupText={t('noGroup')}
      constrainMode={ConstrainMode.horizontalConstrained}
      columns={columns}
      {...props}
    />
  )
}
